import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import './RobotsTxtGenerator.css';

const RobotsTxtGenerator = ({ domain, siteId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [config, setConfig] = useState({});
  const [sitemap, setSitemap] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadAnalysis();
  }, [domain]); // eslint-disable-line react-hooks/exhaustive-deps -- reload when domain changes; siteId from props

  const loadAnalysis = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('domain', domain);
      if (siteId) {
        params.append('siteId', siteId);
      }

      const response = await axios.get(`/api/compliance/robots-txt?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const analysisData = response.data.analysis || {};
      const crawlersData = response.data.crawlers || {};
      
      console.log('RobotsTxtGenerator - API Response:', {
        hasRobotsTxt: response.data.hasRobotsTxt,
        analysis: analysisData,
        crawlers: crawlersData
      });
      
      // Merge analysis with crawlers for easier access
      setAnalysis({
        ...analysisData,
        hasRobotsTxt: response.data.hasRobotsTxt || false,
        crawlers: crawlersData,
      });
      
      // Initialize config from analysis
      const initialConfig = {
        aiCrawlers: {},
        searchEngines: {},
        seoTools: {},
        adCrawlers: {},
      };

      // Set defaults based on analysis - use current state if configured, otherwise use default from CRAWLERS
      if (crawlersData.ai) {
        Object.keys(crawlersData.ai).forEach(key => {
          // If crawler is configured in robots.txt, use its current allowed state
          // Otherwise, use the default allow value from CRAWLERS
          const isConfigured = analysisData.aiCrawlers?.[key]?.configured;
          if (isConfigured) {
            initialConfig.aiCrawlers[key] = analysisData.aiCrawlers[key].allowed;
          } else {
            initialConfig.aiCrawlers[key] = crawlersData.ai[key].allow;
          }
        });
      }

      if (crawlersData.searchEngines) {
        Object.keys(crawlersData.searchEngines).forEach(key => {
          const isConfigured = analysisData.searchEngines?.[key]?.configured;
          if (isConfigured) {
            initialConfig.searchEngines[key] = analysisData.searchEngines[key].allowed;
          } else {
            initialConfig.searchEngines[key] = crawlersData.searchEngines[key].allow;
          }
        });
      }

      if (crawlersData.seoTools) {
        Object.keys(crawlersData.seoTools).forEach(key => {
          const isConfigured = analysisData.seoTools?.[key]?.configured;
          if (isConfigured) {
            initialConfig.seoTools[key] = analysisData.seoTools[key].allowed;
          } else {
            initialConfig.seoTools[key] = crawlersData.seoTools[key].allow;
          }
        });
      }

      if (crawlersData.adCrawlers) {
        Object.keys(crawlersData.adCrawlers).forEach(key => {
          const isConfigured = analysisData.adCrawlers?.[key]?.configured;
          if (isConfigured) {
            initialConfig.adCrawlers[key] = analysisData.adCrawlers[key].allowed;
          } else {
            initialConfig.adCrawlers[key] = crawlersData.adCrawlers[key].allow;
          }
        });
      }

      console.log('RobotsTxtGenerator - Initial Config:', initialConfig);

      setConfig(initialConfig);
      setSitemap(analysisData?.sitemap || `https://${domain}/sitemap.xml`);
    } catch (err) {
      console.error('Error loading robots.txt analysis:', err);
      setError(err.response?.data?.message || 'Failed to load robots.txt analysis. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setResult(null);
    setCopied(false);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/compliance/robots-txt`,
        {
          domain,
          siteId,
          config,
          sitemap,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
    } catch (err) {
      console.error('Error generating optimized robots.txt:', err);
      setError(err.response?.data?.message || 'Failed to generate optimized robots.txt. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (result?.optimizedRobotsTxt) {
      navigator.clipboard.writeText(result.optimizedRobotsTxt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (result?.optimizedRobotsTxt) {
      const blob = new Blob([result.optimizedRobotsTxt], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'robots.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const updateConfig = (category, key, value) => {
    setConfig(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value,
      },
    }));
  };

  const renderCrawlerSection = (title, category, crawlers, analysisData) => {
    if (!crawlers || Object.keys(crawlers).length === 0) {
      return null;
    }

    // Map category names to analysis keys
    const categoryMap = {
      'aiCrawlers': 'ai',
      'searchEngines': 'searchEngines',
      'seoTools': 'seoTools',
      'adCrawlers': 'adCrawlers',
    };
    
    const analysisCategory = categoryMap[category] || category;
    const hasMissing = analysis?.missing?.some(m => m.category === analysisCategory);
    
    // Always show sections - don't hide them
    const isAdCrawlers = category === 'adCrawlers';
    
    return (
      <div className="robots-txt-section">
        <h3>{title}</h3>
        {isAdCrawlers && (
          <p className="section-note" style={{ color: '#059669', fontWeight: 600 }}>
            ✓ Required for ad monetization - these crawlers should always be allowed
          </p>
        )}
        {hasMissing && !isAdCrawlers && (
          <p className="section-note">
            ⚠️ Some {title.toLowerCase()} are not configured in your robots.txt
          </p>
        )}
        <div className="crawler-options">
          {Object.entries(crawlers).map(([key, crawler]) => {
            const currentValue = config[category]?.[key];
            const isConfigured = analysisData?.[key]?.configured;
            const currentAllowed = analysisData?.[key]?.allowed;

            return (
              <div key={key} className={`crawler-option ${!isConfigured ? 'missing' : ''} ${isAdCrawlers ? 'ad-crawler' : ''}`}>
                <label>
                  <span className="crawler-name">{crawler.name}</span>
                  {!isConfigured && !isAdCrawlers && <span className="missing-badge">Not configured</span>}
                  {isConfigured && currentAllowed === false && (
                    <span className="blocked-badge">Currently blocked</span>
                  )}
                  {isConfigured && currentAllowed === true && (
                    <span className="allowed-badge">Currently allowed</span>
                  )}
                  {isAdCrawlers && (
                    <span className="required-badge">Required</span>
                  )}
                  <div className="crawler-controls">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name={`${category}-${key}`}
                        checked={currentValue === true}
                        onChange={() => updateConfig(category, key, true)}
                        disabled={isAdCrawlers}
                      />
                      <span>Allow</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name={`${category}-${key}`}
                        checked={currentValue === false}
                        onChange={() => updateConfig(category, key, false)}
                        disabled={isAdCrawlers}
                      />
                      <span>Block</span>
                    </label>
                  </div>
                </label>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="robots-txt-generator-modal">
        <div className="robots-txt-generator-content">
          <div className="robots-txt-generator-loading">
            <div className="spinner" />
            <p>Analyzing robots.txt...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="robots-txt-generator-modal">
      <div className="robots-txt-generator-content">
        <div className="robots-txt-generator-header">
          <h2>Generate Optimized robots.txt</h2>
          <button className="robots-txt-generator-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="robots-txt-generator-body">
          {error && (
            <div className="robots-txt-generator-error">
              <p>{error}</p>
            </div>
          )}

          {!result && (
            <>
              {!analysis?.hasRobotsTxt && (
                <div className="status-banner info">
                  <p>No robots.txt detected. Configure crawler access below.</p>
                </div>
              )}

              {analysis?.hasRobotsTxt && analysis?.missing?.length > 0 && (
                <div className="status-banner warning">
                  <p>
                    robots.txt detected but missing {analysis.missing.length} configuration{analysis.missing.length > 1 ? 's' : ''}.
                    Configure missing values below.
                  </p>
                </div>
              )}

              {analysis?.hasRobotsTxt && analysis?.missing?.length === 0 && (
                <div className="status-banner success">
                  <p>robots.txt detected with all values configured. You can modify settings below.</p>
                </div>
              )}

              {(analysis || !loading) && (
                <>
                  {renderCrawlerSection(
                    'AI Crawlers',
                    'aiCrawlers',
                    analysis?.crawlers?.ai || {},
                    analysis?.aiCrawlers || {}
                  )}

                  {renderCrawlerSection(
                    'Search Engines',
                    'searchEngines',
                    analysis?.crawlers?.searchEngines || {},
                    analysis?.searchEngines || {}
                  )}

                  {renderCrawlerSection(
                    'SEO Tools',
                    'seoTools',
                    analysis?.crawlers?.seoTools || {},
                    analysis?.seoTools || {}
                  )}

                  {renderCrawlerSection(
                    'Ad Crawlers',
                    'adCrawlers',
                    analysis?.crawlers?.adCrawlers || {},
                    analysis?.adCrawlers || {}
                  )}

                  <div className="robots-txt-section">
                    <h3>Sitemap</h3>
                    <label>
                      <span>Sitemap URL</span>
                      <input
                        type="text"
                        value={sitemap}
                        onChange={(e) => setSitemap(e.target.value)}
                        placeholder={`https://${domain}/sitemap.xml`}
                        className="sitemap-input"
                      />
                    </label>
                    {analysis.hasSitemap && analysis.sitemap && (
                      <p className="current-value">Current: {analysis.sitemap}</p>
                    )}
                  </div>
                </>
              )}

              <div className="robots-txt-generator-actions">
                <button
                  className="btn-generate"
                  onClick={handleGenerate}
                  disabled={generating}
                >
                  {generating ? 'Generating...' : 'Generate Optimized robots.txt'}
                </button>
              </div>
            </>
          )}

          {result && (
            <div className="robots-txt-generator-result">
              <div className="result-summary">
                <h3>Optimization Complete</h3>
                <p>Your optimized robots.txt has been generated.</p>
              </div>

              <div className="robots-txt-generator-output">
                <div className="output-header">
                  <h4>Optimized robots.txt</h4>
                  <div className="output-actions">
                    <button
                      className="btn-copy"
                      onClick={handleCopy}
                      disabled={copied}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                    <button className="btn-download" onClick={handleDownload}>
                      Download
                    </button>
                  </div>
                </div>
                <textarea
                  readOnly
                  value={result.optimizedRobotsTxt}
                  className="robots-txt-output"
                  rows={30}
                />
              </div>

              <div className="robots-txt-generator-instructions">
                <h4>Next Steps</h4>
                <ol>
                  <li>Review the optimized robots.txt above</li>
                  <li>Copy or download the file</li>
                  <li>Upload it to your server at <code>https://{domain}/robots.txt</code></li>
                  <li>Verify it's accessible at that URL</li>
                </ol>
              </div>
            </div>
          )}
        </div>

        <div className="robots-txt-generator-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
          {result && (
            <button className="btn-primary" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

RobotsTxtGenerator.propTypes = {
  domain: PropTypes.string.isRequired,
  siteId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default RobotsTxtGenerator;

